import axios from 'axios';
import { apiClient } from './api-client';

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
    tenantId: string;
    mustChangePassword?: boolean;
  };
}

function getAuthErrorMessage(error: unknown) {
  if (!axios.isAxiosError(error)) {
    return 'Login failed';
  }

  if (error.code === 'ECONNABORTED') {
    return 'The API did not respond. Make sure the local API is running on port 3101.';
  }

  if (!error.response) {
    return 'Could not reach the API. Make sure the local API is running on port 3101.';
  }

  if (error.response.status === 401) {
    return 'Invalid tenant, email, or password.';
  }

  const message = error.response.data?.message;
  if (Array.isArray(message)) {
    return message.join(' ');
  }

  return typeof message === 'string' ? message : 'Login failed';
}

export const authApi = {
  async login(email: string, password: string, tenantSlug: string) {
    try {
      const { data } = await apiClient.post<LoginResponse>('/auth/login', {
        email,
        password,
        tenantSlug,
      }, { timeout: 15000 });
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('auth_refresh_token', data.refresh_token);
      if (data.user?.email) {
        localStorage.setItem('user_email', data.user.email);
      }
      return data;
    } catch (error) {
      throw new Error(getAuthErrorMessage(error));
    }
  },

  logout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('user_email');
  },

  async register(payload: { email: string; password: string; name: string; tenantSlug: string }) {
    const { data } = await apiClient.post('/auth/register', payload);
    return data;
  },

  async requestPasswordReset(email: string, tenantSlug: string) {
    const { data } = await apiClient.post('/auth/request-password-reset', { email, tenantSlug });
    return data;
  },

  async resetPassword(token: string, newPassword: string) {
    const { data } = await apiClient.post('/auth/reset-password', { token, newPassword });
    return data;
  },

  async changePassword(currentPassword: string, newPassword: string) {
    const { data } = await apiClient.post<LoginResponse>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('auth_refresh_token', data.refresh_token);
    if (data.user?.email) {
      localStorage.setItem('user_email', data.user.email);
    }
    return data;
  },

  async verifyEmail(token: string) {
    const { data } = await apiClient.post('/auth/verify-email', { token });
    return data;
  },
};
